/**
 * Choosing the brokerage and the broker from the CRM, on a deal keyed in by hand.
 *
 * Manual entry took the broker as free text, so the same person arrived as
 * "J. Hochman", "Jason Hochman" and "Jason Hochman (Cushman)" and none of them
 * was the CRM's record — "every deal this broker sent" could not be answered.
 * Intake already resolves a broker against the directory (BrokerResolutionCard);
 * this is the same choice offered at entry.
 *
 * Client rulings, 2 Nov 2026 run-through (Chris Young):
 * - Brokerages are a set list in the CRM, each with its affiliated members; a new
 *   firm is added only deliberately (T-21). So the brokerage is searched as it is
 *   typed — typing used to do nothing until the mode was right (T-05).
 * - "Not in CRM" meant "add a new broker to the selected brokerage": the firm
 *   stays and a new person is added under it (T-06).
 * - A broker's first and last names are two fields (T-07).
 * Typed names that are not a CRM record remain only as the fallback for a CRM
 * that cannot be reached, and are said so on the face of the form.
 *
 * The choice rides in the deal's `broker_id`; the names stay in the catalogue's
 * `brokerage_company` and `broker_name`.
 */
import { useId, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Check, Link2, Plus } from 'lucide-react'
import type { AxiosError } from 'axios'

import {
  brokerDirectoryService,
  type BrokerContact,
  type BrokerFirm,
} from '@/services/brokerDirectory'

export type BrokerChoice = {
  /** Catalogue `brokerage_company`. */
  firm: string
  /** Catalogue `broker_name`. */
  contact: string
  /** The CRM contact's id — empty when the names are not a CRM record. */
  brokerId: string
}

type Mode = 'crm' | 'broker' | 'new' | 'free'

type NearFirm = { id: string; name: string; score?: number }

const norm = (text: string) => text.trim().toLowerCase().replace(/&/g, ' and ').replace(/[^\w\s]/g, ' ').replace(/\s+/g, ' ').trim()

export default function BrokerSelect({
  choice,
  onChange,
  className,
}: {
  choice: BrokerChoice
  onChange: (next: BrokerChoice) => void
  /** The form's own input styling, so the widget sits among its neighbours. */
  className: string
}) {
  const queryClient = useQueryClient()
  const listId = useId()
  const firms = useQuery({ queryKey: ['broker-firms'], queryFn: brokerDirectoryService.listFirms, retry: false })
  const contacts = useQuery({ queryKey: ['broker-contacts'], queryFn: brokerDirectoryService.listContacts, retry: false })

  const crmDown = firms.isError || contacts.isError
  const [mode, setMode] = useState<Mode>('crm')
  const [newFirm, setNewFirm] = useState('')
  const [newFirst, setNewFirst] = useState('')
  const [newLast, setNewLast] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [firmQuery, setFirmQuery] = useState(choice.firm)
  const newContact = [newFirst.trim(), newLast.trim()].filter(Boolean).join(' ')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [near, setNear] = useState<NearFirm[] | null>(null)

  const effectiveMode: Mode = crmDown ? 'free' : mode
  const firmList = firms.data ?? []
  const selectedFirm: BrokerFirm | undefined = useMemo(
    () => firmList.find((f) => norm(f.name) === norm(choice.firm)),
    [firmList, choice.firm],
  )
  const firmContacts: BrokerContact[] = useMemo(
    () => (contacts.data ?? []).filter((c) => selectedFirm && c.firmId === selectedFirm.id),
    [contacts.data, selectedFirm],
  )

  const pickFirm = (id: string) => {
    const firm = firmList.find((f) => f.id === id)
    setFirmQuery(firm?.name ?? '')
    // A new firm clears the contact: a broker belongs to ONE firm.
    onChange({ firm: firm?.name ?? '', contact: '', brokerId: '' })
  }
  /** The brokerage as it is typed: the CRM firm it names, or nothing chosen yet. */
  const typeFirm = (text: string) => {
    setFirmQuery(text)
    const firm = firmList.find((f) => norm(f.name) === norm(text))
    if (firm) {
      if (firm.name !== choice.firm) onChange({ firm: firm.name, contact: '', brokerId: '' })
    } else if (choice.firm) {
      onChange({ firm: '', contact: '', brokerId: '' })
    }
  }
  const firmMatches: BrokerFirm[] = useMemo(() => {
    const q = norm(firmQuery)
    if (!q || selectedFirm) return []
    return firmList.filter((f) => norm(f.name).includes(q)).slice(0, 8)
  }, [firmList, firmQuery, selectedFirm])

  /** Add the new person under the brokerage already chosen (T-06). */
  async function addBrokerToFirm() {
    if (!selectedFirm) {
      setError('Choose the brokerage first.')
      return
    }
    if (!newFirst.trim() || !newLast.trim()) {
      setError('Enter the first and last name of the broker.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const created = await brokerDirectoryService.createContact({
        name: newContact, firm: selectedFirm.name, firmId: selectedFirm.id, email: newEmail.trim(),
      })
      await queryClient.invalidateQueries({ queryKey: ['broker-contacts'] })
      onChange({ firm: selectedFirm.name, contact: created.name, brokerId: created.id })
      setNewFirst('')
      setNewLast('')
      setNewEmail('')
      setMode('crm')
    } catch {
      setError('Could not add the broker to the CRM. Nothing was created.')
    } finally {
      setBusy(false)
    }
  }
  const pickContact = (id: string) => {
    const contact = (contacts.data ?? []).find((c) => c.id === id)
    onChange({ firm: choice.firm, contact: contact?.name ?? '', brokerId: contact?.id ?? '' })
  }

  async function addToCrm(force = false) {
    const firmName = newFirm.trim()
    if (!firmName) {
      setError('Enter the brokerage to add.')
      return
    }
    setBusy(true)
    setError(null)
    setNear(null)
    try {
      let firm = firmList.find((f) => norm(f.name) === norm(firmName))
      if (!firm) firm = await brokerDirectoryService.createFirm({ name: firmName, force })
      let created: BrokerContact | null = null
      if (newContact) {
        created = await brokerDirectoryService.createContact({
          name: newContact, firm: firm.name, firmId: firm.id, email: newEmail.trim(),
        })
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['broker-firms'] }),
        queryClient.invalidateQueries({ queryKey: ['broker-contacts'] }),
      ])
      onChange({ firm: firm.name, contact: created?.name ?? '', brokerId: created?.id ?? '' })
      setFirmQuery(firm.name)
      setNewFirm('')
      setNewFirst('')
      setNewLast('')
      setNewEmail('')
      setMode('crm')
    } catch (caught) {
      const response = (caught as AxiosError<{ detail?: unknown }>).response
      const detail = response?.data?.detail as
        | { message?: string; candidates?: NearFirm[]; existing?: NearFirm }
        | string
        | undefined
      if (response?.status === 409 && detail && typeof detail === 'object') {
        if (detail.existing) {
          // Exactly this firm is already in the CRM: use it, do not add it.
          setFirmQuery(detail.existing.name)
          onChange({ firm: detail.existing.name, contact: '', brokerId: '' })
          setMode('crm')
        } else {
          setNear(detail.candidates ?? [])
          setError(detail.message ?? 'A similar brokerage is already in the CRM.')
        }
      } else {
        setError('Could not add to the CRM. Nothing was created.')
      }
    } finally {
      setBusy(false)
    }
  }

  const notInCrm = effectiveMode === 'free' && Boolean(choice.firm.trim() || choice.contact.trim())

  return (
    <div className="col-span-3 space-y-2 rounded-lg border border-amber-400/10 bg-black/10 p-2.5" data-testid="broker-select">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-[10px] text-stone-400">
          <Link2 size={11} /> Brokerage and broker — from the CRM
        </span>
        <div className="flex rounded border border-amber-400/10 p-0.5">
          {(
            [
              ['crm', 'Select from CRM'],
              ['broker', 'Add new broker to brokerage'],
              ['new', 'Add new brokerage'],
            ] as Array<[Mode, string]>
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              disabled={crmDown}
              onClick={() => {
                setMode(value)
                setError(null)
                setNear(null)
              }}
              className={`rounded px-2 py-0.5 text-[9px] disabled:opacity-40 ${
                effectiveMode === value ? 'bg-amber-400/15 text-amber-200' : 'text-stone-500'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {crmDown ? (
        <p className="text-[10px] text-amber-200/80">
          The CRM could not be reached, so names are typed for now — they are not linked to a CRM record.
        </p>
      ) : null}

      {effectiveMode === 'crm' || effectiveMode === 'broker' ? (
        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className="text-[10px] text-stone-500">Brokerage Company</span>
            <input
              aria-label="Brokerage Company"
              value={firmQuery}
              onChange={(e) => typeFirm(e.target.value)}
              list={`${listId}-firms`}
              autoComplete="off"
              placeholder={firms.isLoading ? 'Loading the CRM…' : 'Type to search the CRM brokerages'}
              className={className}
            />
            <datalist id={`${listId}-firms`}>
              {firmList.map((firm) => (
                <option key={firm.id} value={firm.name} />
              ))}
            </datalist>
            {firmMatches.length ? (
              <div className="flex flex-wrap gap-1" data-testid="brokerage-matches">
                {firmMatches.map((firm) => (
                  <button
                    key={firm.id}
                    type="button"
                    onClick={() => pickFirm(firm.id)}
                    className="rounded border border-stone-700 px-1.5 py-0.5 text-[9px] text-stone-300 hover:border-amber-400/40"
                  >
                    {firm.name}
                  </button>
                ))}
              </div>
            ) : firmQuery.trim() && !selectedFirm && !firms.isLoading ? (
              <p className="text-[9px] text-stone-500" data-testid="brokerage-no-match">
                No brokerage in the CRM matches this. Add it with Add new brokerage.
              </p>
            ) : null}
          </label>
          {effectiveMode === 'crm' ? (
            <label className="block space-y-1">
              <span className="text-[10px] text-stone-500">Broker / Intermediary</span>
              <select
                aria-label="Broker / Intermediary"
                value={choice.brokerId}
                onChange={(e) => pickContact(e.target.value)}
                disabled={!selectedFirm}
                className={className}
              >
                <option value="">{selectedFirm ? '— no specific broker —' : 'Pick the brokerage first'}</option>
                {firmContacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>{contact.name}</option>
                ))}
              </select>
            </label>
          ) : (
            <div className="space-y-1">
              <span className="text-[10px] text-stone-500">
                {selectedFirm ? `New broker at ${selectedFirm.name}` : 'New broker — pick the brokerage first'}
              </span>
              <div className="grid grid-cols-2 gap-2">
                <input
                  aria-label="Broker first name"
                  value={newFirst}
                  onChange={(e) => setNewFirst(e.target.value)}
                  placeholder="First name"
                  disabled={!selectedFirm}
                  className={className}
                />
                <input
                  aria-label="Broker last name"
                  value={newLast}
                  onChange={(e) => setNewLast(e.target.value)}
                  placeholder="Last name"
                  disabled={!selectedFirm}
                  className={className}
                />
              </div>
              <input
                aria-label="Broker email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Email (optional)"
                disabled={!selectedFirm}
                className={className}
              />
            </div>
          )}
        </div>
      ) : null}

      {effectiveMode === 'broker' ? (
        <div className="space-y-1">
          {error ? <p className="text-[10px] text-red-300">{error}</p> : null}
          <button
            type="button"
            disabled={busy || !selectedFirm}
            onClick={() => void addBrokerToFirm()}
            className="btn-secondary flex items-center gap-1.5 px-3 py-1 text-[10px] disabled:opacity-50"
          >
            {busy ? 'Adding…' : (<><Plus size={11} /> Add the broker to {selectedFirm?.name ?? 'the brokerage'} and use them</>)}
          </button>
        </div>
      ) : null}

      {effectiveMode === 'new' ? (
        <div className="space-y-2">
          <div className="grid grid-cols-4 gap-2">
            <input
              aria-label="New brokerage name"
              value={newFirm}
              onChange={(e) => setNewFirm(e.target.value)}
              placeholder="Brokerage name"
              className={className}
            />
            <input
              aria-label="New broker first name"
              value={newFirst}
              onChange={(e) => setNewFirst(e.target.value)}
              placeholder="Broker first name (optional)"
              className={className}
            />
            <input
              aria-label="New broker last name"
              value={newLast}
              onChange={(e) => setNewLast(e.target.value)}
              placeholder="Broker last name (optional)"
              className={className}
            />
            <input
              aria-label="New broker email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Broker email (optional)"
              className={className}
            />
          </div>
          {near ? (
            <div className="space-y-1 rounded border border-orange-400/25 bg-orange-400/[0.06] p-2 text-[10px] text-orange-200">
              <div className="flex items-center gap-1.5"><AlertTriangle size={11} /> {error}</div>
              {near.map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  className="mr-2 rounded border border-stone-700 px-2 py-0.5 text-stone-300 hover:border-amber-400/40"
                  onClick={() => {
                    setFirmQuery(candidate.name)
                    onChange({ firm: candidate.name, contact: '', brokerId: '' })
                    setMode('crm')
                    setNear(null)
                    setError(null)
                  }}
                >
                  Use “{candidate.name}”{candidate.score != null ? ` (${Math.round(candidate.score * 100)}% similar)` : ''}
                </button>
              ))}
              <button
                type="button"
                disabled={busy}
                className="rounded border border-orange-400/30 px-2 py-0.5 hover:bg-orange-400/10"
                onClick={() => void addToCrm(true)}
              >
                These are different — add anyway
              </button>
            </div>
          ) : error ? (
            <p className="text-[10px] text-red-300">{error}</p>
          ) : null}
          <button
            type="button"
            disabled={busy}
            onClick={() => void addToCrm()}
            className="btn-secondary flex items-center gap-1.5 px-3 py-1 text-[10px] disabled:opacity-50"
          >
            {busy ? 'Adding…' : (<><Plus size={11} /> Add to the CRM and use it</>)}
          </button>
        </div>
      ) : null}

      {effectiveMode === 'free' ? (
        <div className="grid grid-cols-2 gap-2">
          <label className="block space-y-1">
            <span className="text-[10px] text-stone-500">Brokerage Company</span>
            <input
              aria-label="Brokerage Company"
              value={choice.firm}
              onChange={(e) => onChange({ firm: e.target.value, contact: choice.contact, brokerId: '' })}
              className={className}
            />
          </label>
          <label className="block space-y-1">
            <span className="text-[10px] text-stone-500">Broker / Intermediary</span>
            <input
              aria-label="Broker / Intermediary"
              value={choice.contact}
              onChange={(e) => onChange({ firm: choice.firm, contact: e.target.value, brokerId: '' })}
              className={className}
            />
          </label>
        </div>
      ) : null}

      {notInCrm ? (
        <p
          className="flex items-center gap-1.5 text-[10px] text-amber-200/80"
          data-testid="broker-not-in-crm"
        >
          <AlertTriangle size={11} /> Not in the CRM — stored as typed, and not linked to a CRM record until it is added.
        </p>
      ) : choice.brokerId ? (
        <p className="flex items-center gap-1.5 text-[10px] text-emerald-300/80">
          <Check size={11} /> Linked to the CRM record for {choice.contact}.
        </p>
      ) : null}
    </div>
  )
}
